/**
 * SegmentedControl — `.seg` from the prototype (cms-redesign-mockup.html
 * ~line 843–860). A horizontal pill containing 2–4 button segments. The
 * selected segment fills with navy + white text; the rest sit as light-grey
 * labels that brighten to navy on hover.
 *
 * Each option may carry an optional `count` (rendered as a small JetBrains
 * Mono `.badge`) — useful for status filters like "All / Active / Inactive".
 *
 *   <SegmentedControl
 *     value={activeFilter}
 *     onChange={setActiveFilter}
 *     options={[
 *       { value: 'all',      label: 'All',      count: 247 },
 *       { value: 'active',   label: 'Active',   count: 186 },
 *       { value: 'inactive', label: 'Inactive', count: 61  },
 *     ]}
 *   />
 *
 * Shape can be overridden with `shape="md"` (8px radius) for cases like
 * the List / Grouped view-toggle where the rest of the page uses rounded
 * rectangles rather than pills.
 */
import React from 'react';
import { cn } from './cn';

export function SegmentedControl({
  value,
  onChange,
  options,
  shape = 'pill', // 'pill' | 'md'
  className,
}) {
  const radius = shape === 'pill' ? 999 : 8;
  return (
    // `.seg` (843–847): inline-flex, padding 3, sand border, surface bg,
    // pill radius, soft drop shadow.
    <div
      role="group"
      className={cn(
        'inline-flex items-center bg-surface border border-sand shadow-sm',
        className,
      )}
      style={{ padding: 3, borderRadius: radius }}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange?.(opt.value)}
            aria-pressed={active}
            // `.seg button` (848–855): bare bg/border, text-light 12.5/600,
            // padding 6 14, pill radius, transition all 160ms.
            // `.on` (857): navy bg + white text + soft drop shadow.
            className={cn(
              'inline-flex items-center cursor-pointer',
              'border-0 font-semibold leading-none',
              active
                ? 'bg-navy text-bg'
                : 'bg-transparent text-text-light hover:text-navy',
            )}
            style={{
              gap: 6,
              padding: '6px 14px',
              fontSize: 12.5,
              borderRadius: radius,
              transition: 'all 160ms ease',
              ...(active && { boxShadow: '0 1px 3px #2C3E5020' }),
            }}
          >
            {opt.icon && <span className="shrink-0 inline-flex">{opt.icon}</span>}
            <span>{opt.label}</span>
            {/* `.badge` (858): JetBrains Mono 10.5px / 0.75 opacity. */}
            {opt.count != null && (
              <span
                className="font-mono"
                style={{ fontSize: 10.5, opacity: active ? 0.85 : 0.75 }}
              >
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
